import { gql, useQuery } from "@apollo/client";
import { ProviderLocationQuery } from "~/graphql/__generated__/graphql";
import { Skeleton } from "@mui/material";
import { forwardRef, useMemo } from "react";
import { addDays, startOfDay } from "date-fns";
import {
  coordinateToMapPoint,
  MapMarkerInput,
  MapPoint,
  TrackingMarkerInput,
} from "@caredna-marlin/map-wrapper";
import { default as GenericMapWrapper } from "@caredna-marlin/map-wrapper/google-map-wrapper";
import { Permission, useHasPermission } from "~/hooks/permissions";
import PatientLocationSearchBox from "./findPatientLocation";
import { useNavigate } from "react-router-dom";
import { useMapPatientInfo } from "~/state/map/mapPatientInfo";
import { useOrgConfig } from "~/providers/OrgConfigProvider";
import { routeMarkers } from "@caredna-marlin/utils/client/geocoding";
import {
  RouteLocalCalendarBlock,
  RouteLocalVisitResource,
  RouteLocalVisitResourceWithPatient,
  RouteProviderContext,
  RouteShiftContext
} from "./types";

type RouteIndexViewProps = {
  providerId: number;
  providerName: string;
  date: Date;
  rangeStart?: Date;
  rangeEnd?: Date;
  compact?: boolean;
  localVisitResources?: RouteLocalVisitResource[];
  localCalendarBlocks?: RouteLocalCalendarBlock[];
  localProviderContext: RouteProviderContext;
  localShiftContext?: RouteShiftContext;
  extraMarkers?: MapMarkerInput[];
  showUnsavedChangesNote?: boolean;
  trackingMarkerIcon?: "circle" | "person";
  trackStartPoint?: MapPoint;
};

const TRACKING_MARKER_DEDUPE_EPSILON = 0.0001;

const hasPatientVisit = (resource: RouteLocalVisitResource): resource is RouteLocalVisitResourceWithPatient => {
  return Boolean(resource.visit?.patient);
};

const RouteIndexView = forwardRef<HTMLDivElement, RouteIndexViewProps>(
  ({
    providerId,
    providerName,
    date,
    rangeStart,
    rangeEnd,
    compact = false,
    localVisitResources,
    localCalendarBlocks,
    localProviderContext,
    localShiftContext,
    extraMarkers,
    showUnsavedChangesNote = false,
    trackingMarkerIcon = "circle",
    trackStartPoint,
  }, ref) => {
  const navigate = useNavigate();
  const { GOOGLE_MAPS_WEB_API_KEY } = useOrgConfig();
  const filterStart = startOfDay(date);
  const queryStart = useMemo(() => rangeStart ?? startOfDay(date), [rangeStart, date]);
  const queryEnd = useMemo(() => rangeEnd ?? addDays(startOfDay(date), 1), [rangeEnd, date]);
  const hasProviderLocationPermission = useHasPermission(Permission.viewProviderLocations);

  const { data: locationData, loading: locationLoading } = useQuery<ProviderLocationQuery>(PROVIDER_LOCATIONS_QUERY, {
    variables: {
      start: queryStart.toISOString(),
      end: queryEnd.toISOString(),
      providerId,
    },
    pollInterval: 60000,
    fetchPolicy: "network-only",
    skip: !hasProviderLocationPermission,
  });

  const {mapPatientInfo, loadingPatientInfo, setMapPatientId} = useMapPatientInfo();

  const mapWidth = compact ? "100%" : "75vw";
  const mapHeight = "57vh";
  const markerInputs: MapMarkerInput[] = useMemo(() => {
    const visitResources = localVisitResources ?? [];
    const calendarBlocks = localCalendarBlocks ?? [];

    return [
      ...routeMarkers(
        localProviderContext,
        localShiftContext,
        visitResources.filter(hasPatientVisit),
        calendarBlocks,
      ),
      ...(extraMarkers ?? []),
    ];
  }, [localProviderContext, localShiftContext, localVisitResources, localCalendarBlocks, extraMarkers]);

  const { trackingPoints, lastLocationTimestamp } = useMemo(() => {
    if (!locationData?.location?.length) {
      return { trackingPoints: [], lastLocationTimestamp: undefined };
    }


    const sortedLocations = [...locationData.location].sort((a, b) => 
      new Date(a.timestamp).getTime() - new Date(b.timestamp).getTime()
    );

    const seenCoords = new Set<string>();
    const trackingPointList: MapPoint[] = [];

    if (trackStartPoint) {
      seenCoords.add(`${trackStartPoint.lat},${trackStartPoint.lng}`);
      trackingPointList.push(trackStartPoint);
    }

    // collect all valid tracking points in chronological order
    sortedLocations.forEach((loc) => {
      const point = coordinateToMapPoint(`${loc.latitude},${loc.longitude}`);
      if (point) {
        const coordKey = `${point.lat},${point.lng}`;
        if (!seenCoords.has(coordKey)) {
          seenCoords.add(coordKey);
          trackingPointList.push(point);
        }
      }
    });

    const lastLoc = sortedLocations[sortedLocations.length - 1];
    const latestTimestamp = lastLoc ? new Date(lastLoc.timestamp) : undefined;

    return { trackingPoints: trackingPointList, lastLocationTimestamp: latestTimestamp };
  }, [locationData, trackStartPoint]);

  const trackingMarker = useMemo<TrackingMarkerInput | undefined>(() => {
    if (!lastLocationTimestamp) return undefined;


    const clockStatus = locationData?.latestProviderLocations?.[0]?.clockStatus;
    const isClockedIn = clockStatus === "clock_in";
    const isSosTracking = trackingMarkerIcon === "person";

    if (!isClockedIn && !isSosTracking) {
      return { variant: "hidden", timestamp: lastLocationTimestamp, icon: trackingMarkerIcon };
    }

    const lastPoint = trackingPoints[trackingPoints.length - 1];
    const overlapsHighlightedMarker =
      isSosTracking &&
      lastPoint != null &&
      markerInputs.some(
        (marker) =>
          "highlighted" in marker &&
          marker.highlighted &&
          Math.abs(marker.point.lat - lastPoint.lat) < TRACKING_MARKER_DEDUPE_EPSILON &&
          Math.abs(marker.point.lng - lastPoint.lng) < TRACKING_MARKER_DEDUPE_EPSILON,
      );

    if (overlapsHighlightedMarker) {
      return { variant: "hidden", timestamp: lastLocationTimestamp, icon: trackingMarkerIcon };
    }

    const ageMinutes = (Date.now() - lastLocationTimestamp.getTime()) / (1000 * 60);

    if (ageMinutes >= 4 * 60) {
      return { variant: "hidden", timestamp: lastLocationTimestamp, icon: trackingMarkerIcon };
    }
    
    return {
      variant: ageMinutes > 15 ? "sleeping" : "active",
      timestamp: lastLocationTimestamp,
      icon: trackingMarkerIcon,
    };
  }, [locationData, lastLocationTimestamp, trackingMarkerIcon, trackingPoints, markerInputs]);

  return (
    <div ref={ref}>
      {compact ? null : (
        <div style={{ display: "flex", alignItems: "center" }}>
          <h2>
            {providerName} -{" "}
            {filterStart.toLocaleDateString("en-US", {
              year: "numeric",
              month: "short",
              day: "numeric",
            })}
          </h2>
        </div>
      )}
      {showUnsavedChangesNote ? (
        <p style={{ margin: "0 0 12px", color: "#6b7280", fontSize: "0.875rem" }}>
          This route map includes unsaved changes.
        </p>
      ) : null}
      {compact ? null : (
        <PatientLocationSearchBox mapPatientInfo={mapPatientInfo} setMapPatientId={setMapPatientId} loadingPatientInfo={loadingPatientInfo}/>
      )}
      {!GOOGLE_MAPS_WEB_API_KEY || (locationLoading && !locationData) ? (
        <Skeleton variant="rectangular" width={mapWidth} height={mapHeight} />
      ) : (
        <GenericMapWrapper
          mapsApiKey={GOOGLE_MAPS_WEB_API_KEY}
          displayRoute={true}
          clusterMarkers={true}
          height={mapHeight}
          width={mapWidth}
          markerInputs={markerInputs}
          trackingPoints={trackingPoints}
          trackingMarker={trackingMarker}
          targetLocation={mapPatientInfo?.location}
          onPatientClick={(patientId) => navigate(`/patients/${patientId}`)}
        />
      )}
    </div>
  );
});

const PROVIDER_LOCATIONS_QUERY = gql`
  query ProviderLocation($providerId: Int!, $start: String!, $end: String!)
  @permission(requires: viewProviderLocations)
  {
    location(
      where: {
        providerId: { eq: $providerId }
        timestamp: { gte: $start, lt: $end }
      }
      orderBy: { timestamp: { direction: asc, priority: 1 } }
    ) {
      latitude
      longitude
      timestamp
    }
    latestProviderLocations(providerId: $providerId) {
      clockStatus
      timestamp
    }
  }
`;

export default RouteIndexView;
